import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";

import { ApiError } from "./api/client";
import { pillarFor, type PillarId } from "./app/navigation";
import { AppShell } from "./app/AppShell";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { ContactDetailPage } from "./pages/ContactDetailPage";
import { ContactFormPage } from "./pages/ContactFormPage";
import { ContactsPage } from "./pages/ContactsPage";
import { CustomersPage } from "./pages/CustomersPage";
import { BookingsPage } from "./pages/BookingsPage";
import { CalendarPage } from "./pages/CalendarPage";
import { LeadFormPage } from "./pages/LeadFormPage";
import { LeadsPage } from "./pages/LeadsPage";
import { CatalogPage } from "./pages/CatalogPage";
import { DashboardPage } from "./pages/DashboardPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { PeopleDashboardPage } from "./pages/PeopleDashboardPage";
import { OwnerDeskPage } from "./pages/OwnerDeskPage";
import { ManagerDeskPage } from "./pages/ManagerDeskPage";
import { MyProfilePage } from "./pages/MyProfilePage";
import { MyPayslipsPage } from "./pages/MyPayslipsPage";
import { EMPLOYEE_WRITE_PERSONAS, WORKFORCE_REPORT_PERSONAS } from "./lib/access";
import { personaFor, type Persona } from "./lib/persona";
import { PlannedPage } from "./pages/PlannedPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SetupWizardPage } from "./pages/SetupWizardPage";
import { ProductFormPage } from "./pages/ProductFormPage";
import { SignInPage } from "./pages/SignInPage";
import { SignUpPage } from "./pages/SignUpPage";
import { AcceptInvitePage, VerifyEmailPage } from "./pages/VerifyEmailPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { StaffDeskPage } from "./pages/StaffDeskPage";
import { EmployeesPage } from "./pages/EmployeesPage";
import { EmployeeFormPage } from "./pages/EmployeeFormPage";
import { EmployeeDetailPage } from "./pages/EmployeeDetailPage";
import { OrganizationChartPage, OrganizationPage } from "./pages/OrganizationPage";
import { CheckInPage } from "./pages/CheckInPage";
import { AttendancePage } from "./pages/AttendancePage";
import { AttendanceDetailPage } from "./pages/AttendanceDetailPage";
import { LeavePage } from "./pages/LeavePage";
import { SalaryPage } from "./pages/SalaryPage";
import { PayrollPage } from "./pages/PayrollPage";
import { RosterPage } from "./pages/RosterPage";
import { TasksPage } from "./pages/TasksPage";
import { ReportsPage } from "./pages/ReportsPage";
import { PlatformAuditPage } from "./pages/PlatformAuditPage";
import { PlatformCompaniesPage } from "./pages/PlatformCompaniesPage";
import { InvoicesPage } from "./pages/InvoicesPage";
import { InvoiceDetailPage } from "./pages/InvoiceDetailPage";
import { InvoiceDocumentPage } from "./pages/InvoiceDocumentPage";
import { InvoiceFormPage } from "./pages/InvoiceFormPage";
import { BillingDashboardPage } from "./pages/BillingDashboardPage";
import { BillingReportsPage } from "./pages/BillingReportsPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { TaxesPage } from "./pages/TaxesPage";
import { ReceiptPage } from "./pages/ReceiptPage";
import { CreditNotesPage } from "./pages/CreditNotesPage";
import { CreditNoteFormPage } from "./pages/CreditNoteFormPage";
import { CreditNoteDetailPage } from "./pages/CreditNoteDetailPage";
import { RecurringPage } from "./pages/RecurringPage";
import { RecurringScheduleDetailPage } from "./pages/RecurringScheduleDetailPage";

// No Redux. `react-query` owns server state; component state owns the rest
// (plan.md §3).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Retrying a rejected request cannot fix it, and retrying an auth
        // failure in a loop just locks the user out of the UI.
        if (error instanceof ApiError) {
          if (error.status === 401 || error.status === 403) return false;
          if (error.status >= 400 && error.status < 500) return false;
        }
        return failureCount < 2;
      },
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/signin" element={<SignInRoute />} />
            <Route path="/signup" element={<SignUpRoute />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Auth gate. Renders an Outlet so the shell route below nests
                inside it rather than being rendered a second time. */}
            <Route element={<RequireAuth />}>
              <Route element={<AppShell />}>
                {/* The starting screen and every other screen are shaped by
                    the signed-in persona. Guarding a route here is courtesy,
                    not the boundary: the API independently refuses the data. */}
                <Route index element={<HomeRoute />} />
                <Route path="dashboard" element={guard(["owner", "manager"], <DashboardRoute />)} />

                <Route path="customers">
                  <Route index element={<Navigate to="/customers/contacts" replace />} />
                  <Route path="contacts" element={guard(["owner", "manager"], <ContactsPage />)} />
                  <Route path="contacts/new" element={guard(["owner", "manager"], <ContactFormPage />)} />
                  <Route path="contacts/:id" element={guard(["owner", "manager"], <ContactDetailPage />)} />
                  <Route path="contacts/:id/edit" element={guard(["owner", "manager"], <ContactFormPage />)} />
                  <Route path="trash" element={guard(["owner", "manager"], <ContactsPage showTrash />)} />
                </Route>

                <Route path="customers">
                  <Route index element={<Navigate to="/customers/contacts" replace />} />
                  <Route path="leads" element={guard(["owner", "manager"], <LeadsPage />)} />
                  <Route path="leads/new" element={guard(["owner", "manager"], <LeadFormPage />)} />
                  <Route path="leads/:id/edit" element={guard(["owner", "manager"], <LeadFormPage />)} />
                </Route>

                {/* Pipeline and Activity log are intentionally not built. The
                    nav marks them "Soon"; this route makes the click land on an
                    honest explanation instead of the generic not-found page. */}
                <Route path="customers/*" element={<PlannedSection section="customers" />} />

                <Route path="catalog">
                  <Route index element={guard(["owner", "manager"], <CatalogPage />)} />
                  <Route path="products/new" element={guard(["owner", "manager"], <ProductFormPage />)} />
                  <Route path="products/:id/edit" element={guard(["owner", "manager"], <ProductFormPage />)} />
                </Route>

                <Route path="scheduling/customers" element={guard(["owner", "manager"], <CustomersPage />)} />
                <Route path="scheduling/calendar" element={guard(["owner", "manager"], <CalendarPage />)} />
                <Route path="scheduling/bookings" element={guard(["owner", "manager"], <BookingsPage />)} />
                <Route path="scheduling/*" element={<PlannedSection section="scheduling" />} />

                <Route path="people">
                  <Route index element={<HomeRoute />} />
                  <Route path="desk" element={<PersonaDeskRoute />} />
                  <Route path="me" element={<MyProfilePage />} />
                  <Route
                    path="me/edit"
                    element={guard(
                      ["owner", "manager", "employee"],
                      <EmployeeFormPage selfService />,
                    )}
                  />
                  <Route path="payslips" element={<MyPayslipsPage />} />
                  <Route path="dashboard" element={guard(["owner", "manager"], <PeopleDashboardPage />)} />
                  <Route path="employees" element={guard(["owner", "manager"], <EmployeesPage />)} />
                  <Route path="employees/new" element={guard(EMPLOYEE_WRITE_PERSONAS, <EmployeeFormPage />)} />
                  <Route path="employees/:id" element={<EmployeeDetailPage />} />
                  <Route path="employees/:id/edit" element={guard(EMPLOYEE_WRITE_PERSONAS, <EmployeeFormPage />)} />
                  <Route path="organization" element={guard(["owner", "manager"], <OrganizationPage />)} />
                  <Route path="organization/chart" element={guard(["owner", "manager"], <OrganizationChartPage />)} />
                  <Route path="departments" element={guard(["owner", "manager"], <OrganizationPage initialTab="departments" />)} />
                  <Route path="check-in" element={guard(["owner", "manager"], <CheckInPage />)} />
                  <Route path="attendance" element={<AttendancePage />} />
                  <Route path="attendance/:employeeId" element={<AttendanceDetailPage />} />
                  {/* The daily work report and the attendance register are one
                      page: the same rows, seen two ways. */}
                  <Route path="daily-work-learning" element={<Navigate to="/people/attendance" replace />} />
                  <Route path="daily-work" element={<Navigate to="/people/attendance" replace />} />
                  <Route path="leave" element={<LeavePage />} />
                  <Route path="salary" element={guard(["owner", "manager", "employee"], <SalaryPage />)} />
                  <Route path="payroll" element={guard(["owner", "manager"], <PayrollPage />)} />
                  <Route path="reports" element={guard(WORKFORCE_REPORT_PERSONAS, <ReportsPage />)} />
                  <Route path="roster" element={<RosterPage />} />
                  <Route path="tasks" element={<TasksPage />} />
                </Route>

                <Route path="platform" element={<RequirePlatform />}>
                  <Route index element={<Navigate to="/platform/companies" replace />} />
                  <Route path="companies" element={<PlatformCompaniesPage />} />
                  <Route path="audit" element={<PlatformAuditPage />} />
                </Route>

                <Route path="billing">
                  <Route index element={<Navigate to="/billing/dashboard" replace />} />
                  <Route path="dashboard" element={guard(["owner", "manager"], <BillingDashboardPage />)} />
                  <Route path="reports" element={guard(["owner", "manager"], <BillingReportsPage />)} />
                  <Route path="invoices" element={guard(["owner", "manager"], <InvoicesPage />)} />
                  <Route path="invoices/new" element={guard(["owner", "manager"], <InvoiceFormPage />)} />
                  <Route path="invoices/:id" element={guard(["owner", "manager"], <InvoiceDetailPage />)} />
                  <Route path="invoices/:id/edit" element={guard(["owner", "manager"], <InvoiceFormPage />)} />
                  <Route path="invoices/:id/print" element={guard(["owner", "manager"], <InvoiceDocumentPage />)} />
                  <Route path="taxes" element={guard(["owner", "manager"], <TaxesPage />)} />
                  <Route path="payments" element={guard(["owner", "manager"], <PaymentsPage />)} />
                  <Route path="payments/:id/receipt" element={guard(["owner", "manager"], <ReceiptPage />)} />
                  <Route path="credit-notes" element={guard(["owner", "manager"], <CreditNotesPage />)} />
                  <Route path="credit-notes/new" element={guard(["owner", "manager"], <CreditNoteFormPage />)} />
                  <Route path="credit-notes/:id" element={guard(["owner", "manager"], <CreditNoteDetailPage />)} />
                  <Route path="recurring" element={guard(["owner", "manager"], <RecurringPage />)} />
                  <Route path="recurring/:id" element={guard(["owner", "manager"], <RecurringScheduleDetailPage />)} />
                </Route>
                <Route path="billing/*" element={<PlannedSection section="billing" />} />
                <Route path="automation/*" element={<PlannedSection section="automation" />} />
                <Route path="reports/*" element={<PlannedSection section="reports" />} />

                <Route path="settings" element={guard(["owner"], <SettingsPage />)} />
                <Route path="settings/plan" element={guard(["owner"], <SettingsPage />)} />
                <Route path="setup" element={guard(["owner"], <SetupWizardPage />)} />

                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function RequireAuth() {
  const { user, initialising } = useAuth();

  // Resume runs before first paint resolves. Rendering the sign-in form during
  // that window would flash it at someone who is already signed in.
  if (initialising) return <FullPageLoading />;
  if (!user) return <Navigate to="/signin" replace />;
  return <Outlet />;
}

/** Platform seats stay off-limits to tenant logins, even signed-in ones. */
function RequirePlatform() {
  const { user } = useAuth();
  if (!user?.is_platform_operator) return <Navigate to="/" replace />;
  return <Outlet />;
}

function HomeRoute() {
  return <PersonaDeskRoute />;
}

/**
 * The customer dashboard. It is a distinct destination for every role that can
 * see customers; the role-specific desk lives at `/` and `/people/desk`. Routing
 * the owner to their desk here made the Customers → Dashboard link silently
 * show My Desk instead of customer data.
 */
function DashboardRoute() {
  return <DashboardPage />;
}

/**
 * The role-specific starting screen. Owner, manager and employee each land on
 * a desk built for the decisions they actually make -- not one shared page.
 */
function PersonaDeskRoute() {
  const { user } = useAuth();
  const persona = personaFor(user);
  if (persona === "owner") return <OwnerDeskPage />;
  if (persona === "manager") return <ManagerDeskPage />;
  return <StaffDeskPage />;
}

/**
 * A courtesy guard around a route. It is not the security boundary -- the API
 * refuses the data regardless -- but it keeps an employee from landing on a
 * screen that would only render permission errors.
 */
function RequireRole({ allow, children }: { allow: Persona[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!allow.includes(personaFor(user))) {
    return <Navigate to="/people/desk" replace />;
  }
  return <>{children}</>;
}

function guard(allow: Persona[], element: ReactNode) {
  return <RequireRole allow={allow}>{element}</RequireRole>;
}

function SignInRoute() {
  const { user, initialising } = useAuth();
  if (initialising) return <FullPageLoading />;
  if (user) return <Navigate to="/" replace />;
  return <SignInPage />;
}

function SignUpRoute() {
  const { user, initialising } = useAuth();
  if (initialising) return <FullPageLoading />;
  if (user) return <Navigate to="/" replace />;
  return <SignUpPage />;
}

function FullPageLoading() {
  return (
    <div className="grid min-h-dvh place-items-center bg-slate-50">
      <p className="text-sm text-slate-500">Loading…</p>
    </div>
  );
}

function PlannedSection({ section }: { section: PillarId }) {
  return <PlannedPage pillar={pillarFor(section)} section={section} />;
}